import {Icon, isValidIcon, IconName, DuotoneIconName} from '@benchling/icons';
import React, {HTMLAttributes} from 'react';

import {getAmplitudeProps} from 'analytics/amplitude/getAmplitudeProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import * as logging from 'util/Logging';

import {IconAliasName, isIconAlias, resolveIconAlias} from './IconAliases';

export type BenchlingIconName = IconAliasName | IconName | DuotoneIconName;

export function isValidBenchlingIcon(name: string): name is BenchlingIconName {
  return isIconAlias(name) || isValidIcon(name);
}

export type BenchlingIconProps = HTMLAttributes<HTMLSpanElement> & {
  altText?: string;
  dataTestAttrs?: DataTestAttrs;
  icon: BenchlingIconName;
  ifIconMissingReason?: string;
};

const BenchlingIcon = React.forwardRef<SVGSVGElement, BenchlingIconProps>(
  (props: BenchlingIconProps, ref): JSX.Element => {
    const {ifIconMissingReason, altText, onClick, ...childProps} = props;
    const dataTestAttrs = props.dataTestAttrs ?? {component: 'BenchlingIcon', key: props.icon};
    // Icons inside buttons stay attributed to the button.
    const sharedProps = {
      ...childProps,
      onClick,
      altText,
      ...getAmplitudeProps({includeInClickTracking: onClick != null}),
    };

    /**
     * Note: If you would like to add a new Nucleus UI icon, please reach out in #design-systems
     */
    const resolvedIcon = isIconAlias(props.icon) ? resolveIconAlias(props.icon) : props.icon;
    if (isValidIcon(resolvedIcon)) {
      return <Icon {...sharedProps} icon={resolvedIcon} dataTestAttrs={dataTestAttrs} ref={ref} />;
    } else {
      const reasonString = ifIconMissingReason ? ` ${ifIconMissingReason}.` : '';
      logging.warn(`Icon '${props.icon}' missing.${reasonString} Using notdef by default.`);
      return <Icon {...sharedProps} icon="notdef" ref={ref} />;
    }
  }
);

export default BenchlingIcon;
